import { ProductOperation } from '@components/std-components/ProductOperation'
import { VIPChannel } from '@modules/common/models/enums/VIPChannel'
import {
  ProductCardPlaceholderImage,
  CNFProductCardPlaceholderImg
} from '@resources/images'
import '@styles/ProductCard/style.css'
import classNames from 'classnames'
import React, { FC, useMemo } from 'react'
import { ProductCardSizes } from '@constants'
import { makeStyles } from '@material-ui/core'
import { AttributeDisplayTypes } from '@modules/wholesale/productCardConfigSettings/product-settings-constants'
import { PRODUCT_IMAGE_HEIGHT } from '../../constants/uiConstants'
import EnhancedProductCardWrapper from '@components/std-components/EnhancedProductCardWrapper'
import { productConfiguration } from '@stores/productConfigurationStore'
import { ATTRIBUTE_VALUE_TYPES } from '@utils/attribute-data-formatter'
import { FieldTypeElementMap } from '@modules/wholesale/productCardConfigSettings/Component/AttributeRenderers'

export enum FieldType {
  VALUE = 'value',
  LABEL_AND_VALUE = 'label&value',
  LABEL = 'label',
  DIVIDER = 'divider',
  ELEMENT = 'element'
}

export interface FieldConfig {
  id: string
  type?: FieldType
  label?: string
  value?: string
  element?: React.ElementType
  allowedForChannels: VIPChannel[]
}

const useStyles = makeStyles(theme => ({
  productCard: {
    display: 'flex',
    flexDirection: 'column'
  },
  landscapeProductCard: {
    display: 'flex',
    flexDirection: 'column'
  },
  fitDefaultImage: {
    '& .product-media-img': {
      objectFit: 'cover'
    }
  },
  removeProductDetailsCursor: {
    '& .product-media-img': {
      cursor: 'unset'
    }
  },
  productCardInfoWrapper: {
    width: 'auto',
    height: `calc(100% - ${PRODUCT_IMAGE_HEIGHT}px)`,
    overflow: 'hidden'
  },
  productCardInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 'auto',
    height: `95%`,
    overflow: 'hidden',
    '&:hover': {
      overflowY: 'auto'
    }
  },
  fullHeightProductCardInfo: {
    height: '100%'
  },
  operationContainerClass: {
    maxHeight: 'unset',
    flex: 'unset',
    '& .product-operation-icon-bottom-right': {
      position: 'absolute',
      zIndex: 1,
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bottom: 6,
      right: 6,
      cursor: 'pointer',
      borderRadius: '100%',
      backgroundColor: 'white',
      '& button': {
        padding: 0
      },
      '& button > div': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    },
    '& .product-media-section': {
      width: '100%'
    }
  },
  operationContainerPortraitClass: {
    height: PRODUCT_IMAGE_HEIGHT
  },
  operationContainerLandScapeClass: {
    height: '100%'
  }
}))

export interface ProductCardProps {
  fields: FieldConfig[]
  imageUrl?: string
  size: ProductCardSizes
  topRightIcon?: React.ReactElement
  topLeftIcon?: React.ReactElement
  bottomRightIcon?: React.ReactElement
  isActive: boolean
  onClick: any
  productClasses?: string
  titleRenderer?: React.ElementType
  cardContainerCss?: string
  cardCss?: string
  disabled?: boolean
  showRecommendedIcon?: boolean
  showCoreIcon?: boolean
  showCrossLine?: boolean
  isLeftAlined?: boolean
  isCompressedCard?: boolean
}

export const CNFProductCard: FC<ProductCardProps> = ({
  fields,
  imageUrl,
  size,
  topRightIcon,
  topLeftIcon,
  bottomRightIcon,
  onClick,
  isActive,
  showCrossLine,
  productClasses,
  cardContainerCss = '',
  cardCss = '',
  disabled,
  isLeftAlined = false,
  isCompressedCard = false
}) => {
  const { enhancedViewEnabled, initialAllocationActivated } = productConfiguration
  const classes = useStyles({ hasDefaultImage: !imageUrl })
  const title = fields?.[0]?.value || 'NA'
  const mediaUrlObj = useMemo(
    () =>
      imageUrl
        ? { src: imageUrl, alt: title }
        : {
            src: enhancedViewEnabled
              ? CNFProductCardPlaceholderImg
              : ProductCardPlaceholderImage,
            alt: title
          },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imageUrl]
  )
  const isLandScapeCard = size === ProductCardSizes.Landscape

  return (
    <div
      className={classNames(cardContainerCss, {
        [classes.productCard]: !isLandScapeCard,
        [classes.landscapeProductCard]: isLandScapeCard,
        'card-disabled': disabled,
        [classes.fitDefaultImage]: !imageUrl,
        [classes.removeProductDetailsCursor]: initialAllocationActivated
      })}
    >
      <EnhancedProductCardWrapper
        size={size ? size : ProductCardSizes.Medium}
        isActive={isActive}
        cardCss={cardCss}
      >
        <ProductOperation
          mediaVariant={'card'}
          media={mediaUrlObj}
          bottomRightIcon={bottomRightIcon}
          onClick={onClick}
          topRightIcon={topRightIcon}
          showCrossLine={showCrossLine}
          topLeftIcon={topLeftIcon}
          className={productClasses}
          containerClass={classNames(classes.operationContainerClass, {
            [classes.operationContainerLandScapeClass]: isLandScapeCard,
            [classes.operationContainerPortraitClass]: !isLandScapeCard
          })}
        />
        <div className={classes.productCardInfoWrapper}>
          <div
            className={classNames(classes.productCardInfo, {
              [classes.fullHeightProductCardInfo]: isCompressedCard
            })}
          >
            {fields.map((field: any) => {
              let FieldCmp = null
              if (
                field.valueType === ATTRIBUTE_VALUE_TYPES.CHIP ||
                field.displayType === AttributeDisplayTypes.CHIP
              ) {
                FieldCmp = FieldTypeElementMap[AttributeDisplayTypes.CHIP]
              } else {
                FieldCmp =
                  FieldTypeElementMap[field.displayType] ??
                  FieldTypeElementMap[AttributeDisplayTypes.REGULAR_BODY_TEXT]
              }

              return FieldCmp ? (
                <FieldCmp
                  key={field.attributeId}
                  isLeftAlined={isLeftAlined}
                  isCompressedCard={isCompressedCard}
                  {...field}
                />
              ) : null
            })}
          </div>
        </div>
      </EnhancedProductCardWrapper>
    </div>
  )
}
